import Loader from '@components/shared/loader/Loader';
import { CommonService } from '@services/common/CommonService';
import { loadingUserDealer } from '@store/userDealer/UserDealerSlice';
import { theme } from '@theme';
import { ChangeEvent, FC, memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { VariableSizeList as List } from 'react-window';
import styled from 'styled-components';
import { useIsAdminUser } from '../../hooks/essential-ui/UseIsAdminUser';
import { useCheckDevice } from '../../hooks/UseCheckDevice';
import { GetOneFromPath } from '../../services/api/GenericClient';
import { scrollToTopOnLoad } from '../../services/utils/Helpers';
import { getHeights } from '../../services/utils/SelectStoreHelper';
import { StoreDetails } from '../../types/StoreDetails';
import { Image } from '../style/essential-ui/Image';
import { Icon } from '../style/Icon';
import { Typography } from '../style/Typography';
import AppHeader from './header/AppHeader';
import Store from './Store';
import {
  BadgerDiv,
  CenterAlignedDiv,
  InactiveDealerInput,
  LineHeightDiv,
  LoadingWrapper,
  ResultsDiv,
  SearchDiv,
  ShowCloseIcon,
  ShowInactiveLabel,
  StoreHeaderWrapper,
  StoreListWrapper,
} from './Style';

type SelectStoreProps = {
  openedFromModal: boolean;
  closeModal?: () => void;
};

const SelectStore: FC<SelectStoreProps> = (props) => {
  document.title = 'Mastermind';
  const [isLoading, setIsLoading] = useState(false);
  const [showInactiveStores, setShowInactiveStores] = useState(false);
  const [stores, setStores] = useState(Array<StoreDetails>());
  const [filteredStores, setFilteredStores] = useState(Array<StoreDetails>());
  const [isBadger, setIsBadger] = useState(false);
  const [activeStoresCount, setActiveStoresCount] = useState(0);
  const { isAdminUser, isShadowUser } = useIsAdminUser();
  const [searchTerm, setSearchTerm] = useState('');
  const searchPlaceholder =
    isAdminUser && !isShadowUser ? 'Search store by name or ID' : 'Search store by name';
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isMobileDevice } = useCheckDevice();

  const handleStoreSelectionAndNavigation = (storeCode: string) => {
    if (stores && stores.length > 0) {
      setIsLoading(true);
      const store = stores?.find((s) => s.storeCode === storeCode);
      const selectedStoreName = store?.storeName;
      localStorage.setItem('am.storeCode', storeCode);
      localStorage.setItem('am.storeName', selectedStoreName);
      localStorage.setItem('am.inactiveStore', String(showInactiveStores));
      localStorage.setItem('am.singleActiveStore', String(stores.length === 1));
    }

    CommonService.getHomePageOption().then((result) => {
      let url = '';
      if (isMobileDevice && (result.homepageOption === 0 || result.homepageOption === 1)) {
        url = '/portfolio/all';
      } else {
        switch (result.homepageOption) {
          case 0:
            url = '/portfolio/lease';
            break;
          case 1:
            url = '/portfolio/finance';
            break;
          case 7:
            url = '/portfolio/all';
            break;
          case 8:
            url = '/essential/dashboard';
            break;
          default:
            url = '/portfolio/all';
        }
      }
      dispatch(loadingUserDealer());
      localStorage.setItem('homePageOptionUrl', url);

      const dashboardNav =
        url === '/essential/dashboard'
          ? { state: 'app.essential', route: url }
          : { state: 'app.portfolio', route: url.split('/').pop() };

      localStorage.setItem('lastNavigation', JSON.stringify(dashboardNav));

      navigate(url, { replace: true });

      window.location.reload();
    });
  };

  useEffect(() => {
    const pushOrSmsNotificationUrl = localStorage.getItem('pushOrSmsNotificationUrl');
    if (stores && stores.length > 0) {
      if (pushOrSmsNotificationUrl != null && pushOrSmsNotificationUrl !== '') {
        const pushOrSmsNotificationUrlSplit = pushOrSmsNotificationUrl.split('/');
        const dealerId = pushOrSmsNotificationUrlSplit[pushOrSmsNotificationUrlSplit.length - 3];
        const storeCode = stores.find((store) =>
          store?.storeDealers?.find((dealer) => dealer?.dealerId === dealerId),
        )?.storeCode;
        handleStoreSelectionAndNavigation(storeCode);
      }
      if (stores.length === 1) {
        handleStoreSelectionAndNavigation(stores[0].storeCode);
        localStorage.setItem('am.singleActiveStore', String(true));
      }
    }
  }, [stores]);

  useEffect(() => {
    getStores();
    scrollToTopOnLoad();
  }, []);

  const handleShowInactive = (e: ChangeEvent<HTMLInputElement>) => {
    getStores();
    setShowInactiveStores(e.target.checked);
  };

  const handleBadgerClick = () => {
    setIsBadger(false);
    setSearchTerm('');
    getStores();
  };

  async function getStores() {
    try {
      setIsLoading(true);
      const path = '/common/stores';
      const response = await GetOneFromPath<StoreDetails[]>(path);

      const numberOfActiveStores = response
        .map((e) => e.storeDealers.some((e) => e.isActive))
        .filter((e) => e === true).length;
      setActiveStoresCount(numberOfActiveStores);

      setStores(response);
      !(filteredStores && filteredStores.length) && setFilteredStores(response);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    const filterStores: StoreDetails[] = [];
    if (value !== '') {
      if (value.toLowerCase().replace(' ', '') === 'honeybadger') {
        setFilteredStores([]);
        setIsBadger(true);
      } else {
        setIsBadger(false);
      }
      stores.map((store) => {
        if (
          store.storeName?.toLowerCase().includes(value.toLowerCase()) ||
          (isAdminUser &&
            !isShadowUser &&
            store.storeCode.toLowerCase().includes(value.toLowerCase())) ||
          (isAdminUser &&
            !isShadowUser &&
            store.storeDealers.some((dealer) =>
              dealer.dealerId.toLowerCase().includes(value.toLowerCase()),
            ))
        ) {
          filterStores.push(store);
        }
      });
      setFilteredStores(filterStores);
    } else {
      setFilteredStores(stores);
    }
  };

  const filterStoresByStatus = (allStores) => {
    let stores;
    if (showInactiveStores) {
      stores = allStores.filter((store) => {
        return store.storeDealers.some((dealer) => dealer.isActive === false);
      });
    } else {
      stores = allStores.filter((store) => {
        return store.storeDealers.some((dealer) => dealer.isActive === true);
      });
    }
    return stores;
  };

  const Row = memo(({ index, style }: { index: number; style: React.CSSProperties }) => {
    let stores = filterStoresByStatus(filteredStores);
    const store = stores[index];

    if (store && activeStoresCount > 1) {
      return (
        <div style={style}>
          <Store
            data={store}
            showInactiveStores={showInactiveStores}
            isPrimaryUser={isAdminUser}
            isShadowUser={isShadowUser}
            activeStoresCount={activeStoresCount}
            openedFromModal={props.openedFromModal}
            key={index}
            handleStoreSelectionAndNavigation={handleStoreSelectionAndNavigation}
          />
        </div>
      );
    } else {
      return <></>;
    }
  });

  return localStorage.getItem('pushOrSmsNotificationUrl') ? (
    <Loader coverModal={false} />
  ) : isLoading ? (
    <>
      {!props.openedFromModal && <AppHeader />}
      <LoadingWrapper data-testid='loading-wrapper' openedFromModal={props.openedFromModal}>
        {!props.openedFromModal && (
          <CenterAlignedDiv data-testid='select-text' marginTop={26} marginBottom={8}>
            <Typography
              size={theme.typography.fontSize30}
              fontWeight={theme.typography.fontWeights.semibold}
              color={theme.palette.wetAsphalt}
            >
              Select a Store
            </Typography>
          </CenterAlignedDiv>
        )}
        <Loader coverModal={false} padding={props.openedFromModal ? undefined : '80px 0 0 0'} />
      </LoadingWrapper>
    </>
  ) : (
    <>
      {!props.openedFromModal && <AppHeader />}
      <Wrapper $openedFromModal={props.openedFromModal} data-testid='select-store'>
        <StoreHeaderWrapper openedFromModal={props.openedFromModal} data-testid='store-header'>
          {props.openedFromModal && (
            <ShowCloseIcon data-testid='store-select-close-icon'>
              <Icon
                size={18}
                type={'x'}
                color={theme.palette.black}
                className={'store-select-close-icon'}
                onClick={props.closeModal}
              />
            </ShowCloseIcon>
          )}
          <CenterAlignedDiv data-testid='select-text' marginTop={30} marginBottom={8}>
            <LineHeightDiv>
              <Typography
                size={theme.typography.fontSize30}
                fontWeight={theme.typography.fontWeights.semibold}
                color={theme.palette.wetAsphalt}
              >
                Select a Store
              </Typography>
            </LineHeightDiv>
          </CenterAlignedDiv>
          <CenterAlignedDiv marginBottom={6}>
            {isAdminUser && !isShadowUser && (
              <div>
                <Typography
                  size={theme.typography.h4}
                  fontWeight={theme.typography.fontWeights.normal}
                  color={theme.palette.wetAsphalt}
                >
                  {activeStoresCount} Active Stores&emsp;&bull;&emsp;
                </Typography>
                <ShowInactiveLabel htmlFor='inactive'>
                  <InactiveDealerInput
                    onChange={handleShowInactive}
                    checked={showInactiveStores}
                    id='inactive'
                    type='checkbox'
                  />
                  <Typography
                    size={theme.typography.h4}
                    marginLeft={5}
                    fontWeight={theme.typography.fontWeights.normal}
                    color={theme.palette.wetAsphalt}
                    cursor='pointer'
                  >
                    Show Inactive
                  </Typography>
                </ShowInactiveLabel>
              </div>
            )}
          </CenterAlignedDiv>
          {isAdminUser && !isShadowUser && (
            <SearchDiv
              data-testid='search'
              isPrimaryUser={isAdminUser}
              isShadowUser={isShadowUser}
              isMobileDevice={isMobileDevice}
            >
              <div>
                <Icon
                  className='input-group-prepend'
                  data-testid='store-search'
                  type='loupe'
                  size={20}
                />
                <StyledInput
                  onChange={handleSearch}
                  type='search'
                  name='search'
                  autoComplete='off'
                  autoFocus={!isMobileDevice}
                  value={searchTerm ?? searchTerm}
                  placeholder={searchTerm === '' ? searchPlaceholder : ''}
                  id='search'
                />
              </div>
            </SearchDiv>
          )}
        </StoreHeaderWrapper>
        <StoreListWrapper
          data-testid='store-list'
          isPrimaryUser={isAdminUser}
          isShadowUser={isShadowUser}
          openedFromModal={props.openedFromModal}
        >
          {isBadger ? (
            <BadgerDiv data-testid='badger-rock' openedFromModal={props.openedFromModal}>
              <Image
                className='badger'
                onClick={handleBadgerClick}
                src={'https://static.findmyupgrade.com/badger/rockon.svg'}
                alt='badger-rock'
                data-testid='badger-rock-image'
              />
            </BadgerDiv>
          ) : (
            <ResultsDiv
              data-testid='store-results'
              openedFromModal={props.openedFromModal}
              isMobileDevice={isMobileDevice}
            >
              <List
                width={'100%'}
                height={getHeights(
                  filterStoresByStatus(filteredStores),
                  isAdminUser,
                  isShadowUser,
                  props.openedFromModal,
                )}
                itemCount={filterStoresByStatus(filteredStores)?.length}
                itemSize={() => (isAdminUser && !isShadowUser ? 89 : 60)}
              >
                {Row}
              </List>
            </ResultsDiv>
          )}
        </StoreListWrapper>
      </Wrapper>
    </>
  );
};

export default SelectStore;

type WrapperProps = {
  $openedFromModal?: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  background-color: ${theme.palette.gray};
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  margin: auto;
  padding-top: ${(props) => (props.$openedFromModal ? '0' : '70px')};
  border-radius: ${(props) => (props.$openedFromModal ? '8px 8px 6px 6px' : '')};
  overflow: hidden;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 34px;
  padding: 6px 12px 6px 47px;
  background-color: #fff;
  background-image: none;
  border: 1px solid #ccc;
  font-size: 14px;
  color: #555;
  border-radius: 4px;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;

  &:focus {
    box-shadow:
      inset 0 1px 1px rgba(0, 0, 0, 0.075),
      0 0 8px rgba(85, 52, 140, 0.6);
    border-color: #9b7fc2;
    outline: none;
  }
`;
